// Staff "Prequalification" tab on the Broker / Brokerage detail pages —
// which prequalification site the record uses, and that site's completions.
// Mirrors the C# records in Core/DTOs/Partners/PrequalSiteAdminDtos.cs
// (camelCase wire; enums serialize as strings).
//
// Deliberately standalone: `api/prequalification.ts` is the CROSS-BOOK admin
// queue client, shaped for triage over every submission. This one is scoped to
// a single CRM record and is read-only. Keeping them apart means neither
// surface's types drift because the other grew a field.
//
// ONE deliberate exception: `PrequalSort`. That is a two-literal WIRE TOKEN, not
// a payload shape — it has no field to grow, so the drift the rule above guards
// against cannot happen to it, while a third hand-spelled copy is a real risk
// (its C# counterpart `PrequalListSort` exists for exactly that reason: three
// services spelling `"oldest"` by hand is how one of them starts accepting
// `"asc"`, and a tab whose sections claim one sort control stops agreeing with
// itself). This is a STAFF -> STAFF import; the portal's separate copy in
// `portal/api/prequalSite.ts` stays off-limits, as `portal/isolation.test.ts`
// requires.

import { api } from "@/api/client";
import type {
  PrequalBuyerDocumentPath,
  PrequalSort,
} from "@/api/prequalification";
import type { PagedResult } from "@/types/api";

/** How the record came to have (or not have) a site. Distinguishes an
 *  ambiguous covering firm from "no site", which the PORTAL ownership enum
 *  deliberately refuses to do. */
export type PrequalSiteAdminResolution = "None" | "Own" | "Inherited" | "AmbiguousFirm";

export type PrequalSiteRole = "Buyer" | "Seller";
export type PrequalSiteOutcome = "Pass" | "SoftDecline";

export interface PrequalSiteCard {
  brandId: string;
  slug: string;
  displayName: string;
  referenceCode: string;
  isActive: boolean;
  /** App-relative public funnel route, `/prequalify/{slug}`. */
  publicPath: string;
  /**
   * App-relative branded ad landing page, `/buy/{slug}`.
   *
   * 🔴 Present ONLY for a BUYER-TEAM-owned site. Null for broker-owned,
   * brokerage-owned and ownerless sites, because the branded `/buy` route
   * resolves for that owner and no other — rendering the link anywhere else
   * would advertise a URL that serves the Phoenix-native page. Composed
   * server-side, like `publicPath`, so every staff surface shows the same link.
   */
  buyPublicPath: string | null;
  /** D2a — owned by a buyer team: minted with the team, managed automatically,
   *  rendered read-only (the server refuses edit / unpublish / delete). Optional
   *  so pre-existing fixtures keep compiling; absent reads as false. */
  isTeamOwned?: boolean;
  /** D2a — the row carries the Phoenix skin: its public links serve the native
   *  pages while the slug keeps attribution. */
  usesPhoenixSkin?: boolean;
}

/** One firm a multi-homed broker could be given a shared site under. Populated
 *  ONLY when `resolution === "AmbiguousFirm"`; empty in every other state.
 *
 *  Identity only — whether the firm already has a site is answered by reading
 *  that brokerage's own site through the existing
 *  `prequalSiteAdminApi.forBrokerage`, not duplicated here. */
export interface PrequalSiteCandidateFirm {
  brokerageId: string;
  name: string;
}

export interface PrequalSiteCompletionRow {
  id: string;
  role: PrequalSiteRole;
  firstName: string;
  lastName: string;
  email: string;
  outcome: PrequalSiteOutcome;
  qualifiedAmount?: number | null;
  supportablePriceLow?: number | null;
  supportablePriceHigh?: number | null;
  hasLetter: boolean;
  hasDealSheet: boolean;
  /** Which package this buyer asked for; absent when they were never asked — a
   *  seller, or any buyer from before the fork shipped.
   *
   *  🔴 It is what makes `hasDealSheet` readable. A letter-only buyer reads
   *  `false` there, and without this a staff member on a support call cannot
   *  tell a document that failed to generate from one nobody ever asked for.
   *
   *  The union is IMPORTED from `@/api/prequalification` rather than copied a
   *  third time: both files are staff surfaces with no isolation boundary
   *  between them (this one already imports `PrequalSort` from there), so the
   *  duplication the portal copy earns is not earned here. */
  buyerDocumentPath?: PrequalBuyerDocumentPath | null;
  status: string;
  createdAt: string;
}

export interface BrokerPrequalSiteAdmin {
  resolution: PrequalSiteAdminResolution;
  site?: PrequalSiteCard | null;
  inheritedFromBrokerageId?: string | null;
  inheritedFromBrokerageName?: string | null;
  /** Capped, newest first — see `completionTotal`. On an INHERITED site these
   *  are the FIRM's completions, shared across every broker who inherits it. */
  completions: PrequalSiteCompletionRow[];
  completionTotal: number;
  /** The liaison's "I reviewed which site this broker uses" stamp. Display
   *  only — it gates nothing. */
  inheritConfirmedAt?: string | null;
  /** Firms this broker is multi-homed across, for the create wizard's
   *  ambiguous-affiliation picker. Non-empty ONLY when
   *  `resolution === "AmbiguousFirm"`. Optional so existing fixtures that
   *  predate it keep type-checking. */
  candidateFirms?: PrequalSiteCandidateFirm[] | null;
}

/**
 * A BUYER TEAM's prequalification site as staff see it (D8).
 *
 * `resolution` is `"Own"` or `"None"` ONLY — a team has no covering
 * organization, so it can neither inherit a site nor be ambiguous about which
 * one it would inherit. There is no member-sites footer either: a team's
 * specialists own no sites of their own, so there is no hidden set to reveal.
 *
 * Completions here BECOME BUYER LEADS, routed to this team — the one branded
 * site whose applicants enter the Phoenix pipeline.
 */
export interface BuyerTeamPrequalSiteAdmin {
  resolution: PrequalSiteAdminResolution;
  site?: PrequalSiteCard | null;
  /** Capped, newest first — see `completionTotal`. */
  completions: PrequalSiteCompletionRow[];
  completionTotal: number;
}

export interface BrokerageMemberOwnSite {
  brokerId: string;
  brokerName: string;
  brandId: string;
  slug: string;
}

export interface BrokeragePrequalSiteAdmin {
  /** "Own" or "None" only — a firm never inherits. */
  resolution: PrequalSiteAdminResolution;
  site?: PrequalSiteCard | null;
  completions: PrequalSiteCompletionRow[];
  completionTotal: number;
  /** Active member brokers running their OWN separate site — name + link only.
   *  Staff-only visibility; the partner-facing portal never rolls these up. */
  membersWithOwnSites: BrokerageMemberOwnSite[];
}

/** Re-exported so a consumer of this record-scoped client does not have to reach
 *  into the cross-book queue client for the sort token. `"newest"` | `"oldest"`;
 *  here it orders on the completion's creation, and anything else the server
 *  reads as newest-first. */
export type { PrequalSort };

/** Paging / search / sort for the record-scoped completions list.
 *
 *  **No brand id, deliberately.** The site is resolved server-side from the
 *  broker / brokerage id in the route — a record-scoped read never takes a
 *  caller-supplied site. Every field is optional and the server's defaults match
 *  the window `BrokerPrequalSiteAdmin.completions` already carries, so page 1
 *  can be SEEDED from the site card rather than refetched. */
export interface PrequalSiteCompletionListQuery {
  page?: number;
  pageSize?: number;
  /** Free-text over first name, last name and email. NOT the reference number —
   *  see the C# query record for why this list and the tab's invitations list
   *  have to agree about what one search control means. */
  q?: string;
  sort?: PrequalSort;
}

export const prequalSiteAdminApi = {
  forBroker: (brokerId: string) =>
    api.get<BrokerPrequalSiteAdmin>(`/brokers/${brokerId}/prequalification-site`),
  forBrokerage: (brokerageId: string) =>
    api.get<BrokeragePrequalSiteAdmin>(`/brokerages/${brokerageId}/prequalification-site`),
  /** The buyer team's own site. Gated server-side by `Buyers.Read` — the same
   *  axis as its two siblings, applied to the permission that opens THIS record
   *  kind. Creating and configuring the site stays `Partners.ManageBrands`. */
  forBuyerTeam: (buyerTeamId: string) =>
    api.get<BuyerTeamPrequalSiteAdmin>(`/buyer-teams/${buyerTeamId}/prequalification-site`),

  /** One page of the broker's resolved site's completions. On an INHERITED site
   *  these are the FIRM's — label them brokerage-wide. */
  brokerCompletions: (brokerId: string, query?: PrequalSiteCompletionListQuery) =>
    api.get<PagedResult<PrequalSiteCompletionRow>>(
      `/brokers/${brokerId}/prequalification-site/completions`,
      query,
    ),

  /** One page of the firm's OWN site's completions. A member broker's separate
   *  site is never rolled in. */
  brokerageCompletions: (brokerageId: string, query?: PrequalSiteCompletionListQuery) =>
    api.get<PagedResult<PrequalSiteCompletionRow>>(
      `/brokerages/${brokerageId}/prequalification-site/completions`,
      query,
    ),

  /** One page of the buyer team's OWN site's completions. */
  buyerTeamCompletions: (buyerTeamId: string, query?: PrequalSiteCompletionListQuery) =>
    api.get<PagedResult<PrequalSiteCompletionRow>>(
      `/buyer-teams/${buyerTeamId}/prequalification-site/completions`,
      query,
    ),
};
